export interface ContentProps {
	key: string
	w: number
	h: number
	pageBreakpoints?: number[]
}

export interface PlacedContentProps {
	placementKey: string
	key: string
	colStart: number
	rowStart: number
	w: number
	h: number
	sliceOffsetRows: number
	totalRows: number
}

export const REPORT_PAGE_GRID_COLS = 60
export const REPORT_PAGE_GRID_ROWS = 60
export const REPORT_PAGE_GRID_GAP_PX = 16
export const REPORT_PAGE_BODY_HEIGHT_PX = 2602 - 200 - 40 - 20

export const convertGridRowsToPixels = (
	rowSpan: number,
	{
		bodyHeightPx = REPORT_PAGE_BODY_HEIGHT_PX,
		totalRows = REPORT_PAGE_GRID_ROWS,
		gapPx = REPORT_PAGE_GRID_GAP_PX,
	}: {
		bodyHeightPx?: number
		totalRows?: number
		gapPx?: number
	} = {}
) => {
	if (rowSpan <= 0) {
		return 0
	}

	const totalGapPx = gapPx * (totalRows - 1)
	const rowTrackHeightPx = (bodyHeightPx - totalGapPx) / totalRows

	return rowSpan * rowTrackHeightPx + Math.max(0, rowSpan - 1) * gapPx
}

export const convertGridRowOffsetToPixels = (
	rowOffset: number,
	{
		bodyHeightPx = REPORT_PAGE_BODY_HEIGHT_PX,
		totalRows = REPORT_PAGE_GRID_ROWS,
		gapPx = REPORT_PAGE_GRID_GAP_PX,
	}: {
		bodyHeightPx?: number
		totalRows?: number
		gapPx?: number
	} = {}
) => {
	if (rowOffset <= 0) {
		return 0
	}

	const totalGapPx = gapPx * (totalRows - 1)
	const rowTrackHeightPx = (bodyHeightPx - totalGapPx) / totalRows

	return rowOffset * rowTrackHeightPx + rowOffset * gapPx
}

const createEmptyGrid = (cols: number, rows: number): boolean[][] => {
	return Array.from({ length: rows }, () => Array(cols).fill(false))
}

const fitsInGrid = (
	grid: boolean[][],
	col: number,
	row: number,
	colSpan: number,
	rowSpan: number
): boolean => {
	for (let r = 0; r < rowSpan; r++) {
		for (let c = 0; c < colSpan; c++) {
			const grid_0 = grid[0]
			const grid_rowPlusR = grid[row + r]
			if (
				row + r >= grid.length ||
				grid_0 === undefined ||
				col + c >= grid_0.length ||
				grid_rowPlusR === undefined ||
				grid_rowPlusR[col + c] === undefined ||
				grid_rowPlusR[col + c] === true
			) {
				return false
			}
		}
	}
	return true
}

const placeInGrid = (
	grid: boolean[][],
	col: number,
	row: number,
	colSpan: number,
	rowSpan: number
): void => {
	for (let r = 0; r < rowSpan; r++) {
		for (let c = 0; c < colSpan; c++) {
			const grid_rowPlusR = grid[row + r]
			if (grid_rowPlusR !== undefined) {
				grid_rowPlusR[col + c] = true
			}
		}
	}
}

const findFirstFitPosition = (
	grid: boolean[][],
	colSpan: number,
	rowSpan: number,
	startRow: number
) => {
	for (let r = startRow; r < grid.length; r++) {
		for (let c = 0; c < (grid[0]?.length ?? 0); c++) {
			if (fitsInGrid(grid, c, r, colSpan, rowSpan)) {
				return { col: c, row: r }
			}
		}
	}

	return undefined
}

const getAvailableRowSpan = (
	grid: boolean[][],
	col: number,
	row: number,
	colSpan: number,
	maxRowSpan: number
) => {
	let availableRowSpan = 0
	while (
		availableRowSpan < maxRowSpan &&
		fitsInGrid(grid, col, row, colSpan, availableRowSpan + 1)
	) {
		availableRowSpan++
	}

	return availableRowSpan
}

const getLargestFittingBreakpointSpan = (
	remainingRows: number,
	availableRowSpan: number,
	sliceOffsetRows: number,
	pageBreakpoints: number[] | undefined
) => {
	if (availableRowSpan <= 0) {
		return 0
	}

	if (pageBreakpoints === undefined || pageBreakpoints.length === 0) {
		return Math.min(availableRowSpan, remainingRows)
	}

	const candidateBreakpoints = [...pageBreakpoints, sliceOffsetRows + remainingRows]
		.filter((breakpoint) => breakpoint > sliceOffsetRows)
		.sort((a, b) => a - b)

	let selectedSpan = 0
	for (const breakpoint of candidateBreakpoints) {
		const span = breakpoint - sliceOffsetRows
		if (span <= availableRowSpan) {
			selectedSpan = span
			continue
		}
		break
	}

	return selectedSpan
}

const findFirstSlicePosition = (
	grid: boolean[][],
	colSpan: number,
	maxRowSpan: number,
	startRow: number,
	{
		sliceOffsetRows = 0,
		pageBreakpoints,
	}: {
		sliceOffsetRows?: number
		pageBreakpoints?: number[]
	} = {}
) => {
	for (let r = startRow; r < grid.length; r++) {
		for (let c = 0; c < (grid[0]?.length ?? 0); c++) {
			const availableRowSpan = getAvailableRowSpan(grid, c, r, colSpan, maxRowSpan)
			const sliceRowSpan = getLargestFittingBreakpointSpan(
				maxRowSpan,
				availableRowSpan,
				sliceOffsetRows,
				pageBreakpoints
			)
			if (sliceRowSpan > 0) {
				return {
					col: c,
					row: r,
					rowSpan: sliceRowSpan,
				}
			}
		}
	}

	return undefined
}

export const divideIntoPages = (
	components: ContentProps[],
	cols: number,
	rows: number
): PlacedContentProps[][] => {
	const pages: PlacedContentProps[][] = [[]]
	let grid = createEmptyGrid(cols, rows)
	let currentRow = 0
	let currentPageIndex = 0

	for (const component of components) {
		const hasPageBreakpoints =
			component.pageBreakpoints !== undefined && component.pageBreakpoints.length > 0

		if (component.h <= rows && hasPageBreakpoints === false) {
			let position = findFirstFitPosition(grid, component.w, component.h, currentRow)
			if (position === undefined) {
				currentPageIndex++
				pages[currentPageIndex] = []
				grid = createEmptyGrid(cols, rows)
				currentRow = 0
				position = findFirstFitPosition(grid, component.w, component.h, currentRow)
			}

			if (position === undefined) {
				continue
			}

			placeInGrid(grid, position.col, position.row, component.w, component.h)
			pages[currentPageIndex]?.push({
				placementKey: component.key,
				key: component.key,
				colStart: position.col,
				rowStart: position.row,
				w: component.w,
				h: component.h,
				sliceOffsetRows: 0,
				totalRows: component.h,
			})
			currentRow = position.row
			continue
		}

		let remainingRows = component.h
		let sliceOffsetRows = 0
		let sliceIndex = 0
		let continuationColStart = 0
		let isFirstSlice = true

		while (remainingRows > 0) {
			if (isFirstSlice === true) {
				let firstSlicePosition = findFirstSlicePosition(
					grid,
					component.w,
					remainingRows,
					currentRow,
					{
						sliceOffsetRows,
						pageBreakpoints: component.pageBreakpoints,
					}
				)

				if (firstSlicePosition === undefined) {
					currentPageIndex++
					pages[currentPageIndex] = []
					grid = createEmptyGrid(cols, rows)
					currentRow = 0
					firstSlicePosition = findFirstSlicePosition(
						grid,
						component.w,
						remainingRows,
						currentRow,
						{
							sliceOffsetRows,
							pageBreakpoints: component.pageBreakpoints,
						}
					)
				}

				if (firstSlicePosition === undefined) {
					break
				}

				continuationColStart = firstSlicePosition.col
				placeInGrid(
					grid,
					firstSlicePosition.col,
					firstSlicePosition.row,
					component.w,
					firstSlicePosition.rowSpan
				)
				pages[currentPageIndex]?.push({
					placementKey: `${component.key}__slice_${sliceIndex}`,
					key: component.key,
					colStart: firstSlicePosition.col,
					rowStart: firstSlicePosition.row,
					w: component.w,
					h: firstSlicePosition.rowSpan,
					sliceOffsetRows,
					totalRows: component.h,
				})

				remainingRows -= firstSlicePosition.rowSpan
				sliceOffsetRows += firstSlicePosition.rowSpan
				currentRow = firstSlicePosition.row
				sliceIndex++
				isFirstSlice = false
				continue
			}

			currentPageIndex++
			pages[currentPageIndex] = []
			grid = createEmptyGrid(cols, rows)
			currentRow = 0

			const sliceRowSpan = getLargestFittingBreakpointSpan(
				remainingRows,
				rows,
				sliceOffsetRows,
				component.pageBreakpoints
			)
			if (sliceRowSpan <= 0) {
				break
			}
			placeInGrid(grid, continuationColStart, 0, component.w, sliceRowSpan)
			pages[currentPageIndex]?.push({
				placementKey: `${component.key}__slice_${sliceIndex}`,
				key: component.key,
				colStart: continuationColStart,
				rowStart: 0,
				w: component.w,
				h: sliceRowSpan,
				sliceOffsetRows,
				totalRows: component.h,
			})

			remainingRows -= sliceRowSpan
			sliceOffsetRows += sliceRowSpan
			currentRow = 0
			sliceIndex++
		}
	}

	return pages.filter((page) => page.length > 0)
}
